import React, { useState, useEffect } from 'react';
import { getFeedStatus } from '../services/api/referenceDataApi';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import FieldTypeahead from './shared/FieldTypeahead';

const LookupRuleForm = ({ configuration, onChange, customerId, errors, warnings, availableFields }) => {
  const [feeds, setFeeds] = useState([]);
  const [loading, setLoading] = useState(false);

  const matchTypes = [
    { value: 'EXACT', label: 'Exact Match' },
    { value: 'FUZZY', label: 'Fuzzy Match' }
  ];

  const noMatchBehaviors = [
    { value: 'Review', label: 'Send to Review' },
    { value: 'Skip', label: 'Skip (leave fields unchanged)' },
    { value: 'Default', label: 'Use Default Values' }
  ];

  useEffect(() => {
    if (customerId) {
      fetchFeeds();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  const fetchFeeds = async () => {
    try {
      setLoading(true);
      const response = await getFeedStatus(customerId);
      setFeeds(response.data?.feeds || []);
    } catch (err) {
      console.error('Failed to fetch reference data feeds:', err);
      setFeeds([]);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    const newConfig = { ...configuration };
    newConfig[field] = value;
    onChange(newConfig);
  };

  const handleMatchFieldChange = (index, field, value) => {
    const newConfig = { ...configuration };
    if (!newConfig.matchFields) {
      newConfig.matchFields = [];
    }

    const matchFields = [...newConfig.matchFields];
    if (!matchFields[index]) {
      matchFields[index] = {};
    }

    if (field === 'weight') {
      matchFields[index][field] = parseFloat(value) || 0;
    } else {
      matchFields[index][field] = value;
    }

    newConfig.matchFields = matchFields;
    onChange(newConfig);
  };

  const addMatchField = () => {
    const newConfig = { ...configuration };
    if (!newConfig.matchFields) {
      newConfig.matchFields = [];
    }
    newConfig.matchFields.push({
      sourceField: '',
      referenceField: '',
      matchType: 'FUZZY',
      weight: 1.0
    });
    onChange(newConfig);
  };

  const removeMatchField = (index) => {
    const newConfig = { ...configuration };
    newConfig.matchFields = newConfig.matchFields.filter((_, i) => i !== index);
    onChange(newConfig);
  };

  const handleReturnFieldChange = (index, field, value) => {
    const newConfig = { ...configuration };
    if (!newConfig.returnFields) {
      newConfig.returnFields = [];
    }

    const returnFields = [...newConfig.returnFields];
    if (!returnFields[index]) {
      returnFields[index] = {};
    }

    returnFields[index][field] = value;
    newConfig.returnFields = returnFields;
    onChange(newConfig);
  };

  const addReturnField = () => {
    const newConfig = { ...configuration };
    if (!newConfig.returnFields) {
      newConfig.returnFields = [];
    }
    newConfig.returnFields.push({
      referenceField: '',
      targetField: ''
    });
    onChange(newConfig);
  };

  const removeReturnField = (index) => {
    const newConfig = { ...configuration };
    newConfig.returnFields = newConfig.returnFields.filter((_, i) => i !== index);
    onChange(newConfig);
  };

  const matchFields = configuration.matchFields || [];
  const returnFields = configuration.returnFields || [];
  const totalWeight = matchFields.reduce((sum, field) => sum + (parseFloat(field.weight) || 0), 0);

  return (
    <div>
      {/* Feed Type Selector */}
      <div className="mb-3">
        <label htmlFor="feedType" className="form-label">
          Reference Data Feed <span className="text-danger">*</span>
        </label>
        <select
          id="feedType"
          className={`form-select${errors?.feedType ? ' is-invalid' : ''}`}
          value={configuration.feedType || ''}
          onChange={(e) => handleChange('feedType', e.target.value)}
          required
          disabled={loading}
        >
          <option value="">Select Reference Data Feed</option>
          {feeds.map(feed => (
            <option key={feed.feedId} value={feed.feedType}>
              {feed.displayName} ({feed.feedType})
              {feed.status === 'never-ingested' ? ' — No data loaded' : ''}
            </option>
          ))}
        </select>
        {errors?.feedType && (
          <div className="invalid-feedback">{errors.feedType}</div>
        )}
        {warnings?.feedType && (
          <small className="text-warning d-block mt-1">{warnings.feedType}</small>
        )}
        {loading && (
          <small className="form-text text-muted">Loading feeds...</small>
        )}
        {!loading && feeds.length === 0 && (
          <small className="form-text text-danger">
            No reference data feeds configured for this customer
          </small>
        )}
        {configuration.feedType && (
          <small className="form-text text-success">
            Feed Type: <strong>{configuration.feedType}</strong>
            {feeds.find(f => f.feedType === configuration.feedType)?.enableFuzzyMatching && (
              <span className="ms-2 badge bg-info text-dark">Fuzzy Enabled</span>
            )}
          </small>
        )}
      </div>

      {/* Match Fields */}
      <div className="mb-3">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <label className="form-label mb-0">
            Match Fields <span className="text-danger">*</span>
          </label>
          <button
            type="button"
            className="btn btn-sm btn-outline-primary"
            onClick={addMatchField}
          >
            <FontAwesomeIcon icon={faPlus} /> Add Match Field
          </button>
        </div>

        {errors?.matchFields && (
          <div className="alert alert-danger py-2 mb-2">
            {errors.matchFields}
          </div>
        )}
        {warnings?.matchFields && (
          <div className="text-warning mb-2">
            <small>{warnings.matchFields}</small>
          </div>
        )}

        {matchFields.length === 0 && (
          <div className="alert alert-info">
            No match fields defined. Click "Add Match Field" to specify how to match records.
          </div>
        )}

        {matchFields.map((field, index) => (
          <div key={index} className="card mb-3">
            <div className="card-header d-flex justify-content-between align-items-center">
              <strong>Match Field {index + 1}</strong>
              <button
                type="button"
                className="btn btn-sm btn-outline-danger"
                onClick={() => removeMatchField(index)}
              >
                <FontAwesomeIcon icon={faTrash} />
              </button>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-4 mb-3">
                  <label className="form-label">Source Field</label>
                  <FieldTypeahead
                    id={`matchField-source-${index}`}
                    value={field.sourceField || ''}
                    onChange={(val) => handleMatchFieldChange(index, 'sourceField', val)}
                    suggestions={availableFields || []}
                    placeholder="extracted_vendor_name"
                  />
                  <small className="form-text text-muted">
                    Field from extracted data
                  </small>
                </div>

                <div className="col-md-4 mb-3">
                  <label className="form-label">Reference Field</label>
                  <input
                    type="text"
                    className="form-control"
                    value={field.referenceField || ''}
                    onChange={(e) => handleMatchFieldChange(index, 'referenceField', e.target.value)}
                    placeholder="vendor_name"
                  />
                  <small className="form-text text-muted">
                    Field in reference data
                  </small>
                </div>

                <div className="col-md-2 mb-3">
                  <label className="form-label">Match Type</label>
                  <select
                    className="form-select"
                    value={field.matchType || 'FUZZY'}
                    onChange={(e) => handleMatchFieldChange(index, 'matchType', e.target.value)}
                  >
                    {matchTypes.map(type => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-md-2 mb-3">
                  <label className="form-label">Weight</label>
                  <input
                    type="number"
                    className="form-control"
                    value={field.weight || 1.0}
                    onChange={(e) => handleMatchFieldChange(index, 'weight', e.target.value)}
                    min="0"
                    max="1"
                    step="0.1"
                  />
                  <small className="form-text text-muted">
                    0.0 - 1.0
                  </small>
                </div>
              </div>
            </div>
          </div>
        ))}

        {matchFields.length > 0 && (
          <div className="alert alert-info">
            <strong>Total Weight:</strong> {totalWeight.toFixed(2)}
            {totalWeight !== 1.0 && (
              <span className="text-warning ms-2">
                (Weights should sum to 1.0 for accurate confidence scores)
              </span>
            )}
          </div>
        )}
      </div>

      {/* Return Fields */}
      <div className="mb-3">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <label className="form-label mb-0">
            Return Fields <span className="text-danger">*</span>
          </label>
          <button
            type="button"
            className="btn btn-sm btn-outline-primary"
            onClick={addReturnField}
          >
            <FontAwesomeIcon icon={faPlus} /> Add Return Field
          </button>
        </div>

        {errors?.returnFields && (
          <div className="alert alert-danger py-2 mb-2">
            {errors.returnFields}
          </div>
        )}

        {returnFields.length === 0 && (
          <div className="alert alert-info">
            No return fields defined. Click "Add Return Field" to specify which fields to retrieve.
          </div>
        )}

        {returnFields.map((field, index) => (
          <div key={index} className="card mb-3">
            <div className="card-header d-flex justify-content-between align-items-center">
              <strong>Return Field {index + 1}</strong>
              <button
                type="button"
                className="btn btn-sm btn-outline-danger"
                onClick={() => removeReturnField(index)}
              >
                <FontAwesomeIcon icon={faTrash} />
              </button>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-6 mb-0">
                  <label className="form-label">Reference Field</label>
                  <input
                    type="text"
                    className="form-control"
                    value={field.referenceField || ''}
                    onChange={(e) => handleReturnFieldChange(index, 'referenceField', e.target.value)}
                    placeholder="vendor_code"
                  />
                  <small className="form-text text-muted">
                    Field to retrieve from reference data
                  </small>
                </div>

                <div className="col-md-6 mb-0">
                  <label className="form-label">Target Field</label>
                  <FieldTypeahead
                    id={`returnField-target-${index}`}
                    value={field.targetField || ''}
                    onChange={(val) => handleReturnFieldChange(index, 'targetField', val)}
                    suggestions={availableFields || []}
                    placeholder="lookup_vendor_code"
                  />
                  <small className="form-text text-muted">
                    Field to store the value in
                  </small>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Confidence Threshold */}
      <div className="mb-3">
        <label htmlFor="confidenceThreshold" className="form-label">
          Confidence Threshold
        </label>
        <input
          type="number"
          id="confidenceThreshold"
          className="form-control"
          value={configuration.confidenceThreshold || 0.85}
          onChange={(e) => handleChange('confidenceThreshold', parseFloat(e.target.value) || 0.85)}
          min="0"
          max="1"
          step="0.05"
        />
        <small className="form-text text-muted">
          Minimum confidence score (0.0 - 1.0) required for automatic match.
          Lower scores will trigger manual review. Default: 0.85
        </small>
      </div>

      {/* No-Match Behavior */}
      <div className="mb-3">
        <label htmlFor="noMatchBehavior" className="form-label">
          No-Match Behavior
        </label>
        <select
          id="noMatchBehavior"
          className="form-select"
          value={configuration.noMatchBehavior || 'Review'}
          onChange={(e) => handleChange('noMatchBehavior', e.target.value)}
        >
          {noMatchBehaviors.map(b => (
            <option key={b.value} value={b.value}>{b.label}</option>
          ))}
        </select>
        <small className="form-text text-muted">
          What to do when no reference data record matches the extracted value.
        </small>
      </div>

      {/* Skip If Populated */}
      <div className="mb-3 form-check">
        <input
          type="checkbox"
          id="skipIfPopulated"
          className="form-check-input"
          checked={configuration.skipIfPopulated || false}
          onChange={(e) => handleChange('skipIfPopulated', e.target.checked)}
        />
        <label htmlFor="skipIfPopulated" className="form-check-label">
          Skip if target fields already have values
        </label>
        <small className="form-text text-muted d-block">
          When checked, the lookup will not overwrite fields that already contain data.
        </small>
      </div>

      {/* Lookup Logic Summary */}
      {matchFields.length > 0 && returnFields.length > 0 && configuration.feedType && (
        <div className="alert alert-secondary">
          <strong>Lookup Logic:</strong>
          <div className="mt-2" style={{ fontFamily: 'monospace', fontSize: '0.9em' }}>
            <div>LOOKUP in feed "<strong>{configuration.feedType}</strong>"</div>
            <div className="ms-3">
              MATCH:
              {matchFields.map((field, index) => (
                <div key={index} className="ms-3">
                  - {field.sourceField || 'source'} ≈ {field.referenceField || 'reference'}
                  ({field.matchType || 'FUZZY'}, weight: {field.weight || 1.0})
                </div>
              ))}
            </div>
            <div className="ms-3">
              RETURN:
              {returnFields.map((field, index) => (
                <div key={index} className="ms-3">
                  - {field.referenceField || 'reference'} → {field.targetField || 'target'}
                </div>
              ))}
            </div>
            <div className="ms-3">
              IF confidence &gt;= {configuration.confidenceThreshold || 0.85}
            </div>
            <div className="ms-3">
              ON NO MATCH: {configuration.noMatchBehavior || 'Review'}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LookupRuleForm;
